import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import { chatService } from '../services/chatService'

const SESSION_PAGE_SIZE = 15
const EXCHANGE_PAGE_SIZE = 10

const sortExchanges = (exchanges = []) =>
  [...exchanges].sort(
    (left, right) => left.exchange_number - right.exchange_number,
  )

const mergeExchanges = (current, incoming) => {
  const byId = new Map()
  ;[...current, ...incoming].forEach((exchange) => {
    byId.set(
      exchange.exchange_id || `exchange-${exchange.exchange_number}`,
      exchange,
    )
  })
  return sortExchanges(Array.from(byId.values()))
}

const getSuggestedTitle = (query = '') => {
  const title = query.trim()
  if (title.length <= 60) return title
  const titleAtWordBoundary = title.slice(0, 60).replace(/\s+\S*$/, '')
  return titleAtWordBoundary || title.slice(0, 60)
}

const normalizeExchange = (exchange = {}, fallbackSessionId) => ({
  ...exchange,
  session_id: exchange.session_id || fallbackSessionId || undefined,
})

export const useChatAssistant = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedSessionId = searchParams.get('session')
  const requestedBrand = searchParams.get('brand') || ''
  const [sessions, setSessions] = useState([])
  const [sessionCursor, setSessionCursor] = useState(null)
  const [exchanges, setExchanges] = useState([])
  const [session, setSession] = useState(null)
  const [draft, setDraft] = useState('')
  const [brandSlug, setBrandSlug] = useState(requestedBrand)
  const [listStatus, setListStatus] = useState('ACTIVE')
  const [listError, setListError] = useState('')
  const [chatError, setChatError] = useState('')
  const [isLoadingSessions, setIsLoadingSessions] = useState(true)
  const [isLoadingConversation, setIsLoadingConversation] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [activeAction, setActiveAction] = useState('')
  const [olderCursor, setOlderCursor] = useState(null)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)
  const textareaRef = useRef(null)

  const loadSessions = useCallback(
    async (status = listStatus, cursor = null, append = false) => {
      setListError('')
      setIsLoadingSessions(true)
      try {
        const response = await chatService.listSessions({
          cursor,
          limit: SESSION_PAGE_SIZE,
          status,
        })
        setSessions((current) =>
          append
            ? Array.from(
                new Map(
                  [...current, ...(response.sessions || [])].map((item) => [
                    item.session_id,
                    item,
                  ]),
                ).values(),
              )
            : response.sessions || [],
        )
        setSessionCursor(response.next_cursor || null)
      } catch (error) {
        setListError(
          getApiErrorMessage(
            error,
            'Conversation history could not be loaded.',
          ),
        )
      } finally {
        setIsLoadingSessions(false)
      }
    },
    [listStatus],
  )

  const loadConversation = useCallback(async (sessionId) => {
    if (!sessionId) {
      setSession(null)
      setExchanges([])
      setOlderCursor(null)
      return
    }
    setIsLoadingConversation(true)
    setChatError('')
    try {
      const response = await chatService.getSession(sessionId, {
        limit: EXCHANGE_PAGE_SIZE,
      })
      setSession(response)
      setExchanges(
        sortExchanges(
          (response.exchanges || []).map((exchange) =>
            normalizeExchange(exchange, response.session_id || sessionId),
          ),
        ),
      )
      setOlderCursor(response.next_cursor || null)
      setBrandSlug('')
    } catch (error) {
      setSession(null)
      setExchanges([])
      setChatError(
        getApiErrorMessage(error, 'This conversation could not be opened.'),
      )
    } finally {
      setIsLoadingConversation(false)
    }
  }, [])

  useEffect(() => {
    loadSessions(listStatus)
  }, [listStatus, loadSessions])

  useEffect(() => {
    loadConversation(selectedSessionId)
  }, [loadConversation, selectedSessionId])

  useEffect(() => {
    if (!selectedSessionId) setBrandSlug(requestedBrand)
  }, [requestedBrand, selectedSessionId])

  const startNewConversation = useCallback(() => {
    setSession(null)
    setExchanges([])
    setOlderCursor(null)
    setDraft('')
    setChatError('')
    setBrandSlug(requestedBrand)
    setSearchParams(requestedBrand ? { brand: requestedBrand } : {}, {
      replace: true,
    })
    textareaRef.current?.focus()
  }, [requestedBrand, setSearchParams])

  const openConversation = useCallback(
    (sessionId) => setSearchParams({ session: sessionId }),
    [setSearchParams],
  )

  const isClientGeneratedExchangeId = (value) =>
    typeof value === 'string' && /^(ui-|pending-)/.test(value)

  const matchesExchange = useCallback((item, exchange) => {
    if (
      exchange?.exchange_id &&
      !isClientGeneratedExchangeId(exchange.exchange_id)
    ) {
      return item.exchange_id === exchange.exchange_id
    }
    if (exchange?.ui_key) {
      return item.ui_key === exchange.ui_key
    }
    if (exchange?.client_request_id) {
      return item.client_request_id === exchange.client_request_id
    }
    return (
      item.user_query === exchange?.user_query &&
      item.session_id === exchange?.session_id
    )
  }, [])

  const sendMessage = useCallback(
    async (event) => {
      event?.preventDefault()
      const query = draft.trim()
      if (!query || isSending) return

      setDraft('')
      setChatError('')
      setIsSending(true)
      const optimisticId = `ui-${Date.now()}-${Math.random()}`
      const clientRequestId =
        window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
      setExchanges((current) => [
        ...current,
        {
          ui_key: optimisticId,
          exchange_number:
            (current[current.length - 1]?.exchange_number || 0) + 1,
          user_query: query,
          answer: null,
          status: 'PENDING',
          sources: [],
          client_request_id: clientRequestId,
          session_id: session?.session_id || selectedSessionId || undefined,
        },
      ])

      let activeSession = session
      try {
        if (!activeSession) {
          activeSession = await chatService.createSession(
            brandSlug || undefined,
          )
          setSession(activeSession)
        }
        const messageBrand =
          brandSlug || activeSession?.brand || session?.brand || null
        const answer = messageBrand
          ? await chatService.sendMessage(
              query,
              activeSession.session_id,
              clientRequestId,
              messageBrand,
            )
          : await chatService.sendMessage(
              query,
              activeSession.session_id,
              clientRequestId,
            )
        setExchanges((current) =>
          sortExchanges([
            ...current.filter((exchange) => exchange.ui_key !== optimisticId),
            {
              ...answer,
              user_query: query,
              answer: answer.answer,
              status: answer.answer ? 'ANSWERED' : 'FAILED',
              sources: answer.sources || [],
              session_id:
                activeSession?.session_id ||
                session?.session_id ||
                selectedSessionId,
            },
          ]),
        )
        setSession((current) => ({
          ...current,
          ...activeSession,
          ...answer,
          title: current?.title || getSuggestedTitle(query),
        }))
        setSearchParams(
          { session: activeSession.session_id },
          { replace: true },
        )
        await loadSessions()
      } catch (error) {
        if (activeSession?.session_id) {
          setSearchParams(
            { session: activeSession.session_id },
            { replace: true },
          )
          await loadConversation(activeSession.session_id)
          setExchanges((current) => {
            const matchingExchange = current.find(
              (exchange) =>
                exchange.ui_key === optimisticId ||
                (exchange.user_query === query &&
                  exchange.session_id ===
                    (activeSession?.session_id ||
                      session?.session_id ||
                      selectedSessionId)),
            )
            if (matchingExchange) {
              return current.map((exchange) =>
                matchesExchange(exchange, matchingExchange)
                  ? {
                      ...exchange,
                      client_request_id: clientRequestId,
                      status: 'FAILED',
                    }
                  : exchange,
              )
            }
            return [
              ...current,
              {
                ui_key: optimisticId,
                exchange_number:
                  (current[current.length - 1]?.exchange_number || 0) + 1,
                user_query: query,
                answer: null,
                status: 'FAILED',
                sources: [],
                client_request_id: clientRequestId,
                session_id:
                  activeSession?.session_id ||
                  session?.session_id ||
                  selectedSessionId ||
                  undefined,
              },
            ]
          })
        } else {
          setDraft(query)
          setExchanges((current) =>
            current.map((exchange) =>
              exchange.ui_key === optimisticId
                ? { ...exchange, status: 'FAILED' }
                : exchange,
            ),
          )
        }
        setChatError(
          getApiErrorMessage(
            error,
            'Your question was not answered. You can retry it when the service is available.',
          ),
        )
      } finally {
        setIsSending(false)
        textareaRef.current?.focus()
      }
    },
    [
      brandSlug,
      draft,
      isSending,
      loadConversation,
      loadSessions,
      matchesExchange,
      selectedSessionId,
      session,
      setSearchParams,
    ],
  )

  const loadOlderExchanges = async () => {
    if (!session?.session_id || !olderCursor || isLoadingOlder) return false
    setIsLoadingOlder(true)
    try {
      const response = await chatService.getSession(session.session_id, {
        cursor: olderCursor,
        limit: EXCHANGE_PAGE_SIZE,
      })
      setExchanges((current) =>
        mergeExchanges(
          current,
          (response.exchanges || []).map((exchange) =>
            normalizeExchange(
              exchange,
              response.session_id || session.session_id,
            ),
          ),
        ),
      )
      setOlderCursor(response.next_cursor || null)
      return true
    } catch (error) {
      setChatError(
        getApiErrorMessage(error, 'Earlier messages could not be loaded.'),
      )
      return false
    } finally {
      setIsLoadingOlder(false)
    }
  }

  const retryExchange = useCallback(
    async (exchange) => {
      const retrySessionId =
        exchange.session_id || session?.session_id || selectedSessionId

      if (!retrySessionId) {
        setChatError(
          'This conversation is no longer available. Please reopen it and retry.',
        )
        return
      }

      const retryClientRequestId =
        exchange.client_request_id ||
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random()}`
      const hasExchangeId = Boolean(
        exchange?.exchange_id &&
        !isClientGeneratedExchangeId(exchange.exchange_id),
      )

      setActiveAction(exchange.exchange_id || exchange.ui_key)
      setChatError('')
      setExchanges((current) =>
        current.map((item) =>
          matchesExchange(item, exchange)
            ? { ...item, status: 'PENDING' }
            : item,
        ),
      )

      try {
        const retryBrand = brandSlug || session?.brand || exchange.brand || null
        const response = hasExchangeId
          ? await chatService.retryExchange(exchange.exchange_id)
          : retryBrand
            ? await chatService.sendMessage(
                exchange.user_query,
                retrySessionId,
                retryClientRequestId,
                retryBrand,
              )
            : await chatService.sendMessage(
                exchange.user_query,
                retrySessionId,
                retryClientRequestId,
              )

        setExchanges((current) =>
          current.map((item) =>
            matchesExchange(item, exchange)
              ? {
                  ...item,
                  ...response,
                  client_request_id: retryClientRequestId,
                  exchange_id: response.exchange_id || item.exchange_id,
                  session_id: retrySessionId,
                  status: response.answer ? 'ANSWERED' : 'FAILED',
                  user_query: exchange.user_query,
                }
              : item,
          ),
        )
        await loadSessions()
      } catch (error) {
        setExchanges((current) =>
          current.map((item) =>
            matchesExchange(item, exchange)
              ? { ...item, status: 'FAILED' }
              : item,
          ),
        )
        setChatError(
          getApiErrorMessage(error, 'The question could not be retried.'),
        )
      } finally {
        setActiveAction('')
      }
    },
    [brandSlug, loadSessions, matchesExchange, selectedSessionId, session],
  )

  const rateExchange = async (exchange, feedback) => {
    setActiveAction(exchange.exchange_id)
    setChatError('')
    try {
      const nextFeedback = exchange.feedback === feedback ? null : feedback
      await chatService.rateExchange(exchange.exchange_id, nextFeedback)
      setExchanges((current) =>
        current.map((item) =>
          item.exchange_id === exchange.exchange_id
            ? { ...item, feedback: nextFeedback }
            : item,
        ),
      )
    } catch (error) {
      setChatError(getApiErrorMessage(error, 'Your rating could not be saved.'))
    } finally {
      setActiveAction('')
    }
  }

  const renameConversation = async (title) => {
    if (!title?.trim() || !session?.session_id) return
    try {
      await chatService.renameSession(session.session_id, title.trim())
      setSession((current) => ({ ...current, title: title.trim() }))
      await loadSessions()
      return true
    } catch (error) {
      setChatError(
        getApiErrorMessage(error, 'The conversation could not be renamed.'),
      )
      return false
    }
  }

  const changeArchiveStatus = async (targetSession = session) => {
    const sessionToUpdate = targetSession || session
    if (!sessionToUpdate?.session_id) return

    const status = sessionToUpdate.status === 'ARCHIVED' ? 'ACTIVE' : 'ARCHIVED'

    try {
      await chatService.archiveSession(sessionToUpdate.session_id, status)

      if (sessionToUpdate.session_id === selectedSessionId) {
        if (status === 'ARCHIVED') startNewConversation()
        else setSession((current) => ({ ...current, status }))
      }

      await loadSessions(listStatus)
      return true
    } catch (error) {
      setChatError(
        getApiErrorMessage(error, 'The conversation could not be updated.'),
      )
      return false
    }
  }

  const deleteConversation = async () => {
    if (!session?.session_id) return
    try {
      await chatService.deleteSession(session.session_id)
      startNewConversation()
      await loadSessions(listStatus)
      return true
    } catch (error) {
      setChatError(
        getApiErrorMessage(error, 'The conversation could not be deleted.'),
      )
      return false
    }
  }

  return {
    activeAction,
    brandSlug,
    changeArchiveStatus,
    chatError,
    deleteConversation,
    draft,
    exchanges,
    isLoadingConversation,
    isLoadingOlder,
    isLoadingSessions,
    isSending,
    listError,
    listStatus,
    loadOlderExchanges,
    loadSessions,
    olderCursor,
    openConversation,
    renameConversation,
    retryExchange,
    rateExchange,
    requestedBrand,
    selectedSessionId,
    sendMessage,
    session,
    sessionCursor,
    sessions,
    setBrandSlug,
    setDraft,
    setListStatus,
    setSearchParams,
    startNewConversation,
    textareaRef,
  }
}
